/* eslint-disable max-statements */
/* eslint-disable max-lines */
/* eslint-disable complexity */
import type React from 'react';
import { useCallback, useMemo, useLayoutEffect, useContext } from 'react';
import { CHEVRON_BACK_DS_ID, FOOTER_MENU_CLOSE_DS_ID } from '../constants/index.js';
import type { DSLeftNavigationT } from '../react-desc-prop-types.js';
import LeftNavContext from '../LeftNavigationContext.js';

const nextIndex = (index: number, length: number) => (index + 1) % length;
const prevIndex = (index: number, length: number) => (index - 1 + length) % length;

type UseKeyboardNavigationProps = {
  item: DSLeftNavigationT.GenericItemItemProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (e: any) => void;
};

export const useKeyboardNavigation = ({ item, onClick }: UseKeyboardNavigationProps): React.KeyboardEventHandler => {
  const {
    dsId,
    itemOpts: { selectable, openable, closable },
  } = item;

  const { visibleItems, visibleItemsRefs } = useContext(LeftNavContext);

  const currentIndex = useMemo(
    () => visibleItems.findIndex((visibleItemDsId) => visibleItemDsId === dsId),
    [dsId, visibleItems],
  );

  const { setFocusedItem, focusedItem, setSelectedItem } = useContext(LeftNavContext);

  const focusAndSelect = useCallback(
    (nextDsId: string) => {
      setFocusedItem(nextDsId);
      setSelectedItem(nextDsId);
    },
    [setFocusedItem, setSelectedItem],
  );

  useLayoutEffect(() => {
    if (dsId === focusedItem) visibleItemsRefs.current[dsId]?.current?.focus();
  }, [dsId, focusedItem, visibleItemsRefs]);

  const onKeyDown: React.KeyboardEventHandler = useCallback(
    (e) => {
      const { key } = e;

      // Prevent default behaviour when not tab
      if (key !== 'Tab') {
        e.preventDefault();
      }

      if (dsId && (key === 'Enter' || key === ' ')) {
        if (selectable) {
          onClick(e);
          focusAndSelect(dsId);
        } else if (focusedItem === CHEVRON_BACK_DS_ID) {
          onClick(e);
          focusAndSelect(dsId);
        } else if (focusedItem === FOOTER_MENU_CLOSE_DS_ID) {
          onClick(e);
          setFocusedItem(dsId);
        }
      }

      if (openable && key === 'ArrowRight') {
        onClick(e);
      }

      if (dsId && closable && key === 'ArrowLeft') {
        onClick(e);
        setFocusedItem(dsId);
      }

      if (key === 'ArrowDown') {
        const nextDsId = visibleItems[nextIndex(currentIndex, visibleItems.length)];
        setFocusedItem(nextDsId);
      }

      if (key === 'ArrowUp') {
        const prevDsId = visibleItems[prevIndex(currentIndex, visibleItems.length)];
        setFocusedItem(prevDsId);
      }

      if (key === 'Home') {
        setFocusedItem(visibleItems[0]);
      }

      if (key === 'End') {
        setFocusedItem(visibleItems[visibleItems.length - 1]);
      }
    },
    [
      openable,
      closable,
      selectable,
      focusedItem,
      onClick,
      focusAndSelect,
      dsId,
      setFocusedItem,
      visibleItems,
      currentIndex,
    ],
  );

  return onKeyDown;
};
